import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { sanitizeHtml } from "@/lib/sanitize";

export default function PrivacyPage() {
  const { lang } = useI18n();
  const content = lang === "it" ? IT : EN;
  useDocumentMeta({
    title: lang === "it"
      ? "Privacy Policy · Gallura Drive"
      : "Privacy Policy · Gallura Drive",
    description: lang === "it"
      ? "Informativa sulla privacy di Gallura Drive: trattamento dati personali, GDPR, cookie, diritti dell'utente."
      : "Gallura Drive privacy policy: personal data handling, GDPR compliance, cookies, user rights.",
    canonical: "https://galluradrive.it/privacy",
  });
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#F9F7F3]">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="privacy-back" className="flex items-center gap-2 text-slate-600 hover:brand-text">
            <ArrowLeft className="w-4 h-4" /> Gallura Drive
          </Link>
          <Link to="/terms" className="text-sm text-slate-600 hover:brand-text">Termini</Link>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl brand-text mb-2">{content.title}</h1>
        <p className="text-sm text-slate-500 mb-8">{content.updated}</p>
        <div className="space-y-6 text-slate-700 leading-relaxed" dangerouslySetInnerHTML={sanitizeHtml(content.body)} />
      </article>
      <Footer />
    </div>
  );
}

const IT = {
  title: "Informativa Privacy (GDPR)",
  updated: "Ultimo aggiornamento: febbraio 2026",
  body: `
    <p>Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.lgs. 196/2003 e ss.mm.ii., Le forniamo le seguenti informazioni sul trattamento dei Suoi dati personali.</p>

    <h2 class="font-display text-2xl brand-text mt-8">1. Titolare del trattamento</h2>
    <p><strong>Gallura Drive</strong>, sede in Olbia (SS), P.IVA in corso di registrazione. Contatti: +39 347 001 1246 — info@galluradrive.it</p>

    <h2 class="font-display text-2xl brand-text mt-8">2. Tipologia di dati raccolti</h2>
    <ul class="list-disc pl-6 space-y-1">
      <li>Dati anagrafici: nome e cognome</li>
      <li>Dati di contatto: email, numero di telefono</li>
      <li>Dati di viaggio: data, ora, indirizzo di pickup/dropoff, numero passeggeri, eventuale numero di volo</li>
      <li>Eventuali note libere fornite dall'utente</li>
    </ul>

    <h2 class="font-display text-2xl brand-text mt-8">3. Finalità del trattamento</h2>
    <ul class="list-disc pl-6 space-y-1">
      <li>Gestione della prenotazione e del servizio richiesto (base giuridica: esecuzione del contratto, art. 6.1.b GDPR)</li>
      <li>Adempimento di obblighi di legge fiscale e amministrativi (art. 6.1.c GDPR)</li>
      <li>Comunicazioni operative relative al servizio (conferma, conferma orari, variazioni)</li>
      <li>Risposta a richieste tramite form contatti</li>
    </ul>
    <p>I dati <strong>non</strong> sono utilizzati per profilazione, marketing o cessione a terzi.</p>

    <h2 class="font-display text-2xl brand-text mt-8">4. Modalità di trattamento</h2>
    <p>I dati sono trattati con strumenti elettronici (database cifrato), conservati su server localizzati nell'Unione Europea, protetti da misure tecniche adeguate (cifratura in transito HTTPS, autenticazione amministrativa con password cifrate). Solo il Titolare ha accesso ai dati.</p>

    <h2 class="font-django text-2xl brand-text mt-8">5. Conservazione</h2>
    <p>I dati di prenotazione sono conservati per <strong>10 anni</strong> per obblighi fiscali. I messaggi del form contatti per <strong>24 mesi</strong>. Trascorso tale termine i dati sono cancellati o resi anonimi.</p>

    <h2 class="font-display text-2xl brand-text mt-8">6. Comunicazione a terzi</h2>
    <p>I dati possono essere comunicati a: hosting provider (server UE), servizio di notifica Telegram (chat privata del Titolare), commercialista per adempimenti fiscali. Tutti i fornitori sono nominati Responsabili del Trattamento ai sensi dell'art. 28 GDPR.</p>

    <h2 class="font-display text-2xl brand-text mt-8">7. Diritti dell'interessato</h2>
    <p>Lei ha diritto a: accesso, rettifica, cancellazione, limitazione del trattamento, portabilità, opposizione (artt. 15-22 GDPR). Per esercitarli scrivere a <strong>info@galluradrive.it</strong>. Ha inoltre diritto di proporre reclamo al Garante Privacy (www.garanteprivacy.it).</p>

    <h2 class="font-display text-2xl brand-text mt-8">8. Cookie</h2>
    <p>Il sito utilizza esclusivamente cookie tecnici (preferenza lingua, sessione admin). <strong>Non</strong> sono utilizzati cookie di profilazione, analitici di terze parti o di marketing. Non è quindi richiesto il banner cookie ai sensi delle linee guida del Garante.</p>

    <h2 class="font-display text-2xl brand-text mt-8">9. Conferimento</h2>
    <p>Il conferimento dei dati richiesti nel form di prenotazione è <strong>necessario</strong> per erogare il servizio. Il rifiuto comporta l'impossibilità di completare la prenotazione.</p>
  `,
};

const EN = {
  title: "Privacy Policy (GDPR)",
  updated: "Last updated: February 2026",
  body: `
    <p>Pursuant to EU Regulation 2016/679 (GDPR), this document informs you about the processing of your personal data.</p>

    <h2 class="font-display text-2xl brand-text mt-8">1. Data controller</h2>
    <p><strong>Gallura Drive</strong>, Olbia (SS), Italy. VAT in progress. Contact: +39 347 001 1246 — info@galluradrive.it</p>

    <h2 class="font-display text-2xl brand-text mt-8">2. Data collected</h2>
    <ul class="list-disc pl-6 space-y-1">
      <li>Identification: first and last name</li>
      <li>Contact: email, phone number</li>
      <li>Travel data: date, time, pickup/dropoff address, number of passengers, optional flight number</li>
      <li>Optional free-text notes</li>
    </ul>

    <h2 class="font-display text-2xl brand-text mt-8">3. Purpose</h2>
    <ul class="list-disc pl-6 space-y-1">
      <li>Booking and service management (legal basis: contract performance, art. 6.1.b GDPR)</li>
      <li>Tax and administrative legal obligations (art. 6.1.c GDPR)</li>
      <li>Service-related operational communications</li>
      <li>Reply to contact form requests</li>
    </ul>
    <p>Data is <strong>not</strong> used for profiling, marketing or transfer to third parties.</p>

    <h2 class="font-display text-2xl brand-text mt-8">4. Processing methods</h2>
    <p>Data is processed via electronic tools (encrypted database), stored on EU servers, protected by appropriate technical measures (HTTPS encryption in transit, admin authentication with hashed passwords). Only the Controller has access.</p>

    <h2 class="font-display text-2xl brand-text mt-8">5. Retention</h2>
    <p>Booking data is retained for <strong>10 years</strong> for tax purposes. Contact form messages for <strong>24 months</strong>. After that, data is deleted or anonymized.</p>

    <h2 class="font-display text-2xl brand-text mt-8">6. Disclosure to third parties</h2>
    <p>Data may be shared with: hosting provider (EU servers), Telegram notification service (Controller's private chat), accountant for tax obligations. All vendors are designated as Processors under art. 28 GDPR.</p>

    <h2 class="font-display text-2xl brand-text mt-8">7. Your rights</h2>
    <p>You have the right to: access, rectification, erasure, restriction, portability, objection (art. 15-22 GDPR). To exercise them, write to <strong>info@galluradrive.it</strong>. You may also lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).</p>

    <h2 class="font-display text-2xl brand-text mt-8">8. Cookies</h2>
    <p>The site uses only technical cookies (language preference, admin session). <strong>No</strong> profiling, analytics or marketing cookies are used. Therefore no cookie banner is required.</p>

    <h2 class="font-display text-2xl brand-text mt-8">9. Mandatory data</h2>
    <p>Providing the data requested in the booking form is <strong>required</strong> to deliver the service. Refusal makes the booking impossible.</p>
  `,
};
